import React from 'react';

import PropTypes from 'prop-types';

import * as analytics from '@adsk/acs-web-analytics';
import { TOOL_IDS } from '@adsk/alloy-provider';
import { PhotoIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import { TabTitle } from '@adsk/exo-skeleton-react';

import photosApolloClient from './helpers/gql/createApolloClient';
import { UPDATE_PHOTO, GET_PHOTOS } from './helpers/gql/queries';
import PhotoActivityCard from './PhotoActivityCard';
import PhotoCreate from './PhotoCreate';
import PhotoCustomCreateNewButton from './PhotoCustomCreateNewButton';
import PhotoCustomDataView from './PhotoCustomDataView';
import PhotoCustomTopBar from './PhotoCustomTopBar';
import PhotoEmptyState from './PhotoEmptyState';
import PhotoEntityBlock from './PhotoEntityBlock';
import PhotoEntityCellItem from './PhotoEntityCellItem';
import PhotoEntityPickerItem from './PhotoEntityPickerItem';
import PhotoEntityPreviewModal from './PhotoEntityPreviewModal';
import PhotoFilter from './PhotoFilter';
import PhotoNoSearchResults from './PhotoNoSearchResults';
import PhotoProvider from './PhotoProvider';

const PickerModalTitle = ({ showTabTitle = true }) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const value = T.translate('ADD_PHOTO');

  return (
    <>
      {showTabTitle && <TabTitle value={value} />}
      {value}
    </>
  );
};

PickerModalTitle.propTypes = {
  showTabTitle: PropTypes.bool,
};

export default {
  metadata: {
    domain: 'autodesk-construction-photo',
    type: 'photo',
  },
  is3LOSupported: true,
  renderIcon: (p) => <PhotoIcon {...p} />,
  toolId: TOOL_IDS.PHOTOS,
  showSearchBox: false,
  modalProps: {
    width: 800,
    height: 700,
  },
  PluralText: ({ lowerCase }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const result = T.translate('PHOTOS');
    if (lowerCase && typeof result === 'string') {
      return result.toLowerCase();
    }
    return result;
  },
  PickerModalTitle,
  AddRelationshipsButtonText: ({ count }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return T.translate(
      `ADD_${count > 1 ? 'MULTI_' : count === 1 ? 'ONE_' : ''}BUTTON`,
      { context: count },
    );
  },
  SelectedCount: ({ count }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return T.translate(
      `${count > 1 ? 'MULTI_' : count === 1 ? 'ONE_' : ''}SELECTED_COUNT`,
      { context: count },
    );
  },
  canCreate: () => {
    return Promise.resolve(true);
  },
  CustomCreateNewButton: PhotoCustomCreateNewButton,
  CreateNewEntityPanel: PhotoCreate,
  queryEntities: (query) =>
    photosApolloClient
      .query({
        query: GET_PHOTOS,
        variables: {
          projectId: query.projectId,
          lastCursor: query.lastCursor,
          limit: query.pageLimit,
          ...query.filters,
        },
        partialRefetch: true,
      })
      .then(
        ({
          data: {
            ggProjectByUid: { photos },
          },
        }) => {
          const lastCursor =
            photos.edges.length > 0 &&
            photos.edges[photos.edges.length - 1].cursor;
          return {
            data: photos.edges.map((p) => p.node),
            nextPageQuery: photos.pageInfo.hasNextPage && {
              ...query,
              lastCursor,
            },
          };
        },
      ),
  getEntitiesByIds: (query) =>
    photosApolloClient
      .query({
        query: GET_PHOTOS,
        variables: {
          projectId: query.projectId,
          uids: query.ids,
          limit: query.ids.length,
        },
        partialRefetch: true,
      })
      .then(
        ({
          data: {
            ggProjectByUid: { photos },
          },
        }) => ({
          data: photos.edges.map((p) => p.node),
        }),
      ),
  EntityBlock: PhotoEntityBlock,
  EntityPickerItem: PhotoEntityPickerItem,
  EntityPreviewModal: PhotoEntityPreviewModal,
  FiltersPanel: PhotoFilter,
  Provider: PhotoProvider,
  EntityMenuItem: PhotoEntityCellItem,
  CustomPickerNoSearchResults: PhotoNoSearchResults,
  ActivityCard: PhotoActivityCard,
  CustomPickerEmptyState: PhotoEmptyState,
  CustomDataView: PhotoCustomDataView,
  CustomTopBar: PhotoCustomTopBar,
  onRemove: (relationship, photo) => {
    const sourceEntity = relationship.entities.find(
      (entity) => entity.type !== 'photo',
    );
    analytics.track('photo.unlink', {
      source: sourceEntity && sourceEntity.type,
      photo_uid: photo.id,
      image_type: photo.imageType,
    });
  },
  onAddRelationships: (source, entities, projectId) => {
    const uids = entities.map((entity) => entity.id);

    uids.forEach((uid) => {
      photosApolloClient.mutate({
        mutation: UPDATE_PHOTO,
        variables: {
          input: {
            uid,
            projectUid: projectId,
            type: source.type,
          },
        },
      });
    });
  },
};
